import type { Controls } from 'three'
import type { ControlMode } from '@/shared/types/core'
import type { ControlManager } from '@/shared/types/services'
import type { EngineFrameContext } from '@/shared/types/view'

/**
 * 代理型 ControlManager，将调用转发至当前活动视图的 ControlManager。
 */
export class ActiveViewControlManagerFacade implements ControlManager {
  constructor(private readonly getActiveManager: () => ControlManager) {}

  /** 当前活动控制模式 */
  get activeMode(): ControlMode {
    return this.getActiveManager().activeMode
  }

  /** 当前活动控制器；无活动模式返回 null */
  get activeController(): Controls<any> | null {
    return this.getActiveManager().activeController
  }

  /**
   * 注册控制器到当前活动视图。
   *
   * @param mode - 控制模式
   * @param control - 控制器实例
   */
  register(mode: ControlMode, control: Controls<any>): void {
    this.getActiveManager().register(mode, control)
  }

  /**
   * 注销当前活动视图中的控制器。
   *
   * @param mode - 控制模式
   * @returns 成功移除返回 true
   */
  unregister(mode: ControlMode): boolean {
    return this.getActiveManager().unregister(mode)
  }

  /**
   * 切换控制模式。
   *
   * @param mode - 目标模式
   */
  setMode(mode: ControlMode): void {
    this.getActiveManager().setMode(mode)
  }

  /**
   * 按模式获取已注册控制器。
   *
   * @param mode - 控制模式
   * @returns 控制器实例；未注册返回 undefined
   */
  getController(mode: ControlMode): Controls<any> | undefined {
    return this.getActiveManager().getController(mode)
  }

  setInputActive(active: boolean): void {
    this.getActiveManager().setInputActive(active)
  }

  /**
   * 每帧更新活动控制器。
   *
   * @param frame - 引擎帧上下文
   */
  update(frame: EngineFrameContext): void {
    this.getActiveManager().update(frame)
  }

  /** 释放代理（空实现，由各视图 ControlManager 自行释放） */
  dispose(): void {}
}
